package org.zq.astrock_ai.network

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.zq.astrock_ai.api.StockCodes

data class SectorStrength(
    val name: String,
    val changePercent: Double,
    val type: String,
)

data class StockMarketContext(
    val totalMarketCap: Double? = null,
    val turnoverAmount: Double? = null,
    val todayMainNet: Double? = null,
    val recentMainNet: Double? = null,
    val turnoverRate: Double? = null,
    val changePercent: Double? = null,
    val industry: String = "",
    val concepts: List<String> = emptyList(),
    val market: String = "",
    val strongSectors: List<SectorStrength> = emptyList(),
)

/**
 * 东方财富个股资金、概念及板块强弱数据。
 * 金额单位均为元；recentMainNet 为最近 5 个交易日主力净流入合计。
 */
class EastMoneyMarketContextClient(
    private val client: HttpClient = createMarketHttpClient(),
) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun fetch(code: String): StockMarketContext = coroutineScope {
        val snapshot = async { runCatching { fetchSnapshot(code) }.getOrDefault(StockMarketContext()) }
        val concepts = async { runCatching { fetchConcepts(code) }.getOrDefault(emptyList()) }
        val funds = async { runCatching { fetchFundFlow(code) }.getOrDefault(null to null) }
        val industries = async { runCatching { fetchTopSectors(2, "行业") }.getOrDefault(emptyList()) }
        val conceptRanks = async { runCatching { fetchTopSectors(3, "概念") }.getOrDefault(emptyList()) }

        val base = snapshot.await()
        val (todayMain, recentMain) = funds.await()
        base.copy(
            todayMainNet = todayMain,
            recentMainNet = recentMain,
            concepts = concepts.await(),
            market = classifyMarket(code),
            strongSectors = (industries.await() + conceptRanks.await())
                .sortedByDescending { it.changePercent }
                .take(8),
        )
    }

    private suspend fun fetchSnapshot(code: String): StockMarketContext {
        val text = client.get(STOCK_URL) {
            parameter("secid", StockCodes.toSecId(code))
            parameter("fltt", 2)
            parameter("fields", "f48,f116,f117,f127,f168,f170")
            parameter("ut", TOKEN)
            header("User-Agent", UA)
        }.bodyAsText()
        val data = json.parseToJsonElement(text).jsonObject["data"]?.jsonObject
            ?: return StockMarketContext()
        return StockMarketContext(
            totalMarketCap = data.number("f116"),
            turnoverAmount = data.number("f48"),
            turnoverRate = data.number("f168"),
            changePercent = data.number("f170"),
            industry = data.text("f127"),
        )
    }

    private suspend fun fetchConcepts(code: String): List<String> {
        val text = client.get(SECTOR_LIST_URL) {
            parameter("secid", StockCodes.toSecId(code))
            parameter("fields", "f12,f14")
            parameter("spt", 3)
            parameter("invt", 2)
            parameter("fltt", 2)
            parameter("pi", 0)
            parameter("po", 1)
            parameter("np", 1)
            parameter("ut", TOKEN)
            header("User-Agent", UA)
        }.bodyAsText()
        val diff = json.parseToJsonElement(text).jsonObject["data"]
            ?.jsonObject
            ?.get("diff")
            ?: return emptyList()
        return diff.items()
            .mapNotNull { it.jsonObject.text("f14").takeIf(String::isNotBlank) }
            .filterNot { it.endsWith("板块") || it.startsWith("昨日") || it.startsWith("最近") }
            .distinct()
            .take(8)
    }

    private suspend fun fetchFundFlow(code: String): Pair<Double?, Double?> {
        val text = client.get(FUND_FLOW_URL) {
            parameter("secid", StockCodes.toSecId(code))
            parameter("klt", 101)
            parameter("lmt", 5)
            parameter("fields1", "f1,f2,f3,f7")
            parameter("fields2", "f51,f52,f53,f54,f55,f56,f57")
            header("User-Agent", UA)
        }.bodyAsText()
        val lines = json.parseToJsonElement(text).jsonObject["data"]
            ?.jsonObject
            ?.get("klines") as? JsonArray
            ?: return null to null
        val values = lines.mapNotNull {
            it.jsonPrimitive.contentOrNull?.split(",")?.getOrNull(1)?.toDoubleOrNull()
        }
        return values.lastOrNull() to values.takeLast(5).takeIf { it.isNotEmpty() }?.sum()
    }

    private suspend fun fetchTopSectors(type: Int, label: String): List<SectorStrength> {
        val text = client.get(SECTOR_RANK_URL) {
            parameter("pn", 1)
            parameter("pz", 10)
            parameter("po", 1)
            parameter("np", 1)
            parameter("fltt", 2)
            parameter("invt", 2)
            parameter("fid", "f3")
            parameter("fs", "m:90+t:$type")
            parameter("fields", "f12,f14,f3")
            header("User-Agent", UA)
        }.bodyAsText()
        val diff = json.parseToJsonElement(text).jsonObject["data"]
            ?.jsonObject
            ?.get("diff")
            ?: return emptyList()
        return diff.items().mapNotNull { element ->
            val item = element.jsonObject
            val name = item.text("f14")
            val change = item.number("f3")
            if (name.isBlank() || change == null) null else SectorStrength(name, change, label)
        }
    }

    private fun JsonObject.number(key: String): Double? =
        get(key)?.jsonPrimitive?.doubleOrNull

    private fun JsonObject.text(key: String): String =
        get(key)?.jsonPrimitive?.contentOrNull.orEmpty().takeUnless { it == "-" } ?: ""

    private fun JsonElement.items(): List<JsonElement> = when (this) {
        is JsonArray -> toList()
        is JsonObject -> values.toList()
        else -> emptyList()
    }

    private fun classifyMarket(code: String): String = when {
        code.startsWith("688") -> "科创板"
        code.startsWith("300") || code.startsWith("301") -> "创业板"
        code.startsWith("8") || code.startsWith("4") -> "北交所"
        code.startsWith("6") -> "沪市主板"
        code.startsWith("0") || code.startsWith("2") -> "深市主板"
        else -> "A股"
    }

    companion object {
        private const val STOCK_URL = "https://push2.eastmoney.com/api/qt/stock/get"
        private const val SECTOR_LIST_URL = "https://push2.eastmoney.com/api/qt/slist/get"
        private const val SECTOR_RANK_URL = "https://push2.eastmoney.com/api/qt/clist/get"
        private const val FUND_FLOW_URL =
            "https://push2his.eastmoney.com/api/qt/stock/fflow/daykline/get"
        private const val TOKEN = "fa5fd1943c7b386f172d6893dbfba10b"
        private const val UA =
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    }
}
